import type { AuthMeResponse, User } from '@/shared/types';
import { getJson, performTokenRefresh, postJson } from '@/shared/api/apiClient';

const AUTH_BASE = '/api/v1/auth';

// Asks the backend for the Google consent URL (it owns client_id, scopes, state).
export async function getGoogleAuthUrl(): Promise<string> {
  const { auth_url } = await getJson<{ auth_url: string }>(`${AUTH_BASE}/google/login`);
  return auth_url;
}

// Uses the HttpOnly refresh cookie to mint a new access token. Returns the token
// on success, null when the engine rejected the session, and THROWS when the
// request itself failed to travel — callers decide what a network failure means
// for them (boot treats it as signed out; requests surface it as their own
// error). Deduped via apiClient.
export function refreshAccessToken(): Promise<string | null> {
  return performTokenRefresh();
}

// Best-effort: revokes the server-side refresh token and clears the cookie.
export async function logout(): Promise<void> {
  try {
    await postJson<void>(`${AUTH_BASE}/logout`, undefined, {
      credentials: 'include',
      skipAuthRefresh: true,
    });
  } catch {
    // Logout is best-effort; the client clears its own token regardless.
  }
}

// Fetches the authenticated profile and maps it onto the app's User shape.
export async function fetchMe(): Promise<User> {
  const me = await getJson<AuthMeResponse>(`${AUTH_BASE}/me`);
  return {
    // Fall back to the email when Google didn't supply a display name, so the
    // user chip never renders blank.
    userId: me.id,
    displayName: me.name?.trim() || me.email,
    email: me.email,
    avatarUrl: me.avatar_url,
    role: me.role ?? 'user',
  };
}
